import { z } from "zod";

/**
 * An optional quantity/cost socket.
 *
 * Same string-from-NUMERIC problem as `QUANTITY_INPUT`, but for fields a source
 * may legitimately omit. A blank string is treated as absent rather than zero —
 * a missing minimum-order quantity and a minimum of 0 mean different things to
 * the replenishment engine, and coercing one into the other is how a supplier
 * silently becomes orderable in single units.
 */
export const OPTIONAL_QUANTITY_INPUT = z
  .preprocess((value) => {
    // A mapped SQL NULL arrives as `null`, not `undefined`: the intended source
    // is `observe_data_source_link`, whose per-column arrays come straight from
    // node-postgres, and `use_data_mapping` cells are the same. `.optional()`
    // accepts undefined but REJECTS null — and batch validation runs over the
    // whole pending chunk and throws, so one nullable column would fail every
    // row in the batch before the handler ever ran.
    if (value === null) return undefined;
    if (typeof value === "string") {
      return value.trim() === "" ? undefined : Number(value);
    }
    return value;
    // The trailing `.optional()` is NOT redundant with the inner one. Socket
    // generation decides `required` from the OUTER zod node only
    // (`isFieldOptional` in utils/socket-type-inference.ts), and `z.preprocess`
    // yields a ZodPipe — so an inner `.optional()` is invisible to it and every
    // socket here generated as required. That made Save & Publish fail with
    // "missing input" on eleven sockets the schema calls optional.
  }, z.number().optional())
  .optional();

/**
 * An optional text socket that tolerates a mapped SQL NULL, for the same reason
 * as `OPTIONAL_QUANTITY_INPUT`. Blank is treated as absent so a padded
 * fixed-width field does not become an empty-string name.
 */
export const OPTIONAL_TEXT_INPUT = z
  .preprocess((value) => {
    if (value === null) return undefined;
    if (typeof value === "string" && value.trim() === "") return undefined;
    return value;
    // Outer `.optional()` for the same reason as OPTIONAL_QUANTITY_INPUT above.
  }, z.string().optional())
  .optional();

/**
 * An optional text socket that also accepts a NUMBER, stringified.
 *
 * Sources hand identifiers and packed dates over as integers as often as text —
 * an AS/400 `YYYYMMDD` date column and a numeric vendor code both arrive as
 * `number` — and a plain string socket makes those edges unmappable. The
 * conversion happens here rather than in the handler so the socket still types
 * as `string` for edge compatibility.
 */
export const OPTIONAL_NUMERIC_TEXT_INPUT = z
  .preprocess((value) => {
    if (value === null) return undefined;
    if (typeof value === "number") {
      return Number.isFinite(value) ? String(value) : undefined;
    }
    if (typeof value === "string" && value.trim() === "") return undefined;
    return value;
  }, z.string().optional())
  .optional();
